import React, { Fragment } from 'react'
import { css } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { theme as deprecatedTheme } from '../../../styled'
import {
  Application,
  UserInput,
  AttackEventDetails,
  StackFrame,
  getUserInputValue
} from '../shared'
import {
  PossibleFindings,
  AttackEventCodeLocation,
  AttackEventSuspiciousQueryElements
} from '../attack-event-details'
import {
  CustomCodeAccessingSystemFilesDetailsExamples,
  CommonFileExploitsDetailsExamples,
  PathTraversalFileSecurityBypassDetailsExamples
} from '../attack-event-details-examples'
import { CodeSnippet } from '../../../legacy-design-system/code-snippet'
import { WithRedactedTooltip } from '../with-redacted-tooltip'
import { FormattedMessage } from 'react-intl'

const possibleFindings: PossibleFindings = {
  CUSTOM_CODE_ACCESSING_SYSTEM_FILES:
    CustomCodeAccessingSystemFilesDetailsExamples,
  COMMON_FILE_EXPLOITS: CommonFileExploitsDetailsExamples
}

export interface AttackEventDetailsPathTraversalProps {
  readonly application: Application
  readonly userInput?: UserInput
  readonly details: AttackEventDetails
  readonly file: string
  readonly method: string
  readonly stackFrames: StackFrame[]
  readonly line: number
  readonly lastCustomFrame: number
}

export function AttackEventDetailsPathTraversal({
  file,
  method,
  stackFrames,
  userInput,
  application,
  details: { findings },
  line,
  lastCustomFrame
}: AttackEventDetailsPathTraversalProps) {
  return (
    <Fragment>
      <AttackEventCodeLocation
        file={file}
        method={method}
        stackFrames={stackFrames}
        line={line}
        lastCustomFrame={lastCustomFrame}
      />
      <CodeSnippet
        labelId="title_attackEventDetails_suspiciousFilepath"
        className={css`
          color: ${theme.color.red50};
          margin-bottom: 1.5em;
        `}
        labelClassName={css`
          font-weight: ${deprecatedTheme.fontWeights.primary.bold};
        `}
      >
        <WithRedactedTooltip text={getUserInputValue(userInput)} />
      </CodeSnippet>
      {Array.isArray(findings) && (
        <AttackEventSuspiciousQueryElements
          language={application.language}
          labelId="variable_attackEventDetails_pathContainsSuspiciousElements"
          findings={findings}
          possibleFindings={possibleFindings}
        />
      )}
    </Fragment>
  )
}

// We don't know if the shape will be correct yet
type DetailsInput =
  | undefined
  | {
      readonly path?: string
    }

// The required shape to render this rule
interface DetailsValidated {
  readonly path: string
}

// Type guard to validate the shape of the details object
function isDetails(details: DetailsInput): details is DetailsValidated {
  return (
    !!details &&
    details.hasOwnProperty('path') &&
    typeof details.path === 'string'
  )
}

export const containerStyles = css`
  margin: 1em 0 2em 0;
  word-break: break-word;
`

interface Props {
  readonly userInput?: UserInput
  readonly callLocation?: string
  readonly details?: {
    readonly path?: string
  }
  readonly file?: string
  readonly method?: string
  readonly stackFrames?: StackFrame[]
}

// This is slightly different than other "attack event details" for semantic rules since this
// path traversal semantic rule only has one finding.
export function AttackEventDetailsPathTraversalFileSecurityBypass({
  file,
  method,
  stackFrames,
  userInput,
  callLocation,
  details
}: Props) {
  if (!isDetails(details)) {
    throw new Error('details is not valid')
  }
  if (typeof callLocation !== 'string') {
    throw new Error('callLocation is invalid')
  }
  return (
    <Fragment>
      <AttackEventCodeLocation
        file={file}
        method={method}
        stackFrames={stackFrames}
      />
      <CodeSnippet
        labelId="title_attackEventDetails_suspiciousFilepath"
        className={css`
          color: ${theme.color.red50};
          margin-bottom: 1.5em;
        `}
        labelClassName={css`
          font-weight: ${deprecatedTheme.fontWeights.primary.bold};
        `}
      >
        <WithRedactedTooltip text={getUserInputValue(userInput)} />
      </CodeSnippet>

      <div
        className={css`
          margin-bottom: 0.5em;
          color: ${theme.color.neutral100};
        `}
      >
        <FormattedMessage
          id="variable_attackEventDetails_pathContainsSuspiciousElements"
          values={{ count: 1 }}
        />
      </div>
      <PathTraversalFileSecurityBypassDetailsExamples />
    </Fragment>
  )
}
